import type { ElitePrimeInsightCta } from '../../../types/elitePrimeSummary.types'
import { lightImpactVibrate, postCTA as postCta } from '../../../utils/appBridge'
import { useElitePlansLanding } from '../ElitePlansLandingContext'
import { ELITE_PLANS_ELITE_PRIME_TAB_ID } from '../constants/planIds'
import { ElitePlansHero } from './hero/ElitePlansHero'
import { ElitePlansAllocationPreview } from './ElitePlansAllocationPreview'
import { ElitePlansAdviceCarousel } from './ElitePlansAdviceCarousel'
import { ElitePlansRebalancingPlanSection } from './ElitePlansRebalancingPlanSection'
import { ElitePlansFeaturesSection } from './ElitePlansFeaturesSection'
import { ElitePlansCompareAccordion } from './ElitePlansCompareAccordion/ElitePlansCompareAccordion'
import { ElitePlansSectionSeparator } from './ElitePlansSectionSeparator'
import CardsTrustMarker from '../../../components/elite/CardsTrustMarker'
import VideoTrustMarker from '../../../components/elite/VideoTrustMarker'
import { ElitePlansTrustStatement } from './ElitePlansTrustStatement'

/** Sell landing body — section order matches the Elite / Elite Prime screens. Family purchase lives on `/elite-plans-family-landing`. */
export function ElitePlansSellBody() {
  const { activeTab, status } = useElitePlansLanding()
  const isElitePrimeTab = activeTab.id === ELITE_PLANS_ELITE_PRIME_TAB_ID
  // Clears fixed buy CTA + FAB (About Elite / Prime Assistant). Trust statement already has pb-70.
  const lastSectionBottomPx = isElitePrimeTab ? 72 : 88

  return (
    <>
      <ElitePlansHero />
      <div className='bg-[color:var(--ep-below-hero)]'>
        {/* Figma: Compare sits first in below-fold, above allocation / advice / features */}
        <ElitePlansCompareAccordion />
        <ElitePlansSectionSeparator withMargin={false} />
        <ElitePlansAllocationPreview />
        {/* Elite: SEBI rebalancing above advice carousel (separator only when rebalancing shows) */}
        <ElitePlansRebalancingPlanSection />
        <ElitePlansAdviceCarousel />
        {isElitePrimeTab && status?.elitePrimeInsights?.globalInvestCta ? (
          <div className='mt-8 px-5'>
            <ElitePrimeGlobalInvestBanner cta={status.elitePrimeInsights.globalInvestCta} />
          </div>
        ) : null}
        {isElitePrimeTab ? (
          <div className='mt-8 px-5'>
            <img
              src='/images/elite-plans/elite-prime/prime-personal-coach-banner.png'
              alt='Prime exclusive. Offer extended to next 10K users. Our CEO and Product Head as your personal coach. Email them directly for 1-to-1 guidance on your portfolio.'
              width={335}
              height={162}
              className='block h-auto w-full'
            />
          </div>
        ) : null}
        <ElitePlansFeaturesSection />
        {/* CardsTrustMarker bleeds with -mx-5, so it needs a px-5 parent. Video is Elite-only. */}
        <div className='my-12 flex flex-col gap-6 px-5'>
          {isElitePrimeTab ? null : <VideoTrustMarker />}
          <CardsTrustMarker />
        </div>
        <div style={{ paddingBottom: lastSectionBottomPx }}>
          <ElitePlansTrustStatement />
        </div>
      </div>
    </>
  )
}

function ElitePrimeGlobalInvestBanner({ cta }: { cta: ElitePrimeInsightCta }) {
  return (
    <button
      type='button'
      className='block w-full !rounded-2xl !border-0 !bg-transparent !p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-prime30'
      onClick={() => {
        lightImpactVibrate()
        postCta(JSON.stringify(cta))
      }}
    >
      <img
        src='/images/elite-plans/elite-prime/global-invest-banner.png'
        alt='Prime Exclusive. Invest in Global Funds. Access global opportunities through GIFT City with our exclusive concierge service. View Details.'
        width={335}
        height={202}
        className='block h-auto w-full'
      />
    </button>
  )
}
